
"use client"

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

interface SidebarProviderProps {
  children: React.ReactNode
  defaultCollapsed?: boolean
}

interface SidebarContextProps {
  collapsed: boolean
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>
}

const SidebarContext = React.createContext<SidebarContextProps>({
  collapsed: false,
  setCollapsed: () => undefined,
})

export function SidebarProvider({
  children,
  defaultCollapsed = false,
}: SidebarProviderProps) {
  const [collapsed, setCollapsed] = React.useState<boolean>(defaultCollapsed)

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed }}>
      {children}
    </SidebarContext.Provider>
  )
}

export const useSidebar = () => React.useContext(SidebarContext)

const sidebarVariants = cva(
  "h-screen group flex flex-col gap-4 border-r overflow-hidden bg-background transition-all duration-300 ease-in-out relative",
  {
    variants: {
      variant: {
        default: "w-[240px]",
        collapsed: "w-[70px]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface SidebarProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof sidebarVariants> {}

export const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(
  ({ className, ...props }, ref) => {
    const { collapsed } = useSidebar()

    return (
      <aside
        ref={ref}
        className={cn(
          sidebarVariants({
            variant: collapsed ? "collapsed" : "default",
          }),
          className
        )}
        {...props}
      />
    )
  }
)

Sidebar.displayName = "Sidebar"

export const SidebarTrigger = ({
  className,
}: React.HTMLAttributes<HTMLButtonElement>) => {
  const { collapsed, setCollapsed } = useSidebar()

  return (
    <button
      type="button"
      className={cn(
        "h-9 w-9 flex items-center justify-center rounded-md border transition-colors hover:bg-muted",
        className
      )}
      onClick={() => setCollapsed(!collapsed)}
      aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
    >
      <svg
        className={cn("h-4 w-4 transition-transform", {
          "rotate-180": !collapsed,
        })}
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 512 512"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path
          d="M 198.608,85.321 113.928,170 l 84.68,84.679 19.626,-19.626 -54.951,-54.951 328.35,0 0,-40.205 -328.35,0 54.951,-54.95 -19.626,-19.626 z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  )
}

SidebarTrigger.displayName = "SidebarTrigger"

export const SidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { collapsed } = useSidebar()

  return (
    <div
      ref={ref}
      className={cn(
        "flex items-center h-14 px-4",
        collapsed && "justify-center",
        className
      )}
      {...props}
    />
  )
})

SidebarHeader.displayName = "SidebarHeader"

export const SidebarContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn("flex flex-col flex-1 overflow-auto", className)}
      {...props}
    />
  )
})

SidebarContent.displayName = "SidebarContent"

export const SidebarFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return <div ref={ref} className={cn("h-14", className)} {...props} />
})

SidebarFooter.displayName = "SidebarFooter"
